import gsap, { Flip } from "@/lib/gsap";
import { useAppSelector } from "@/lib/redux/hooks";
import { ProductFilter } from "@/lib/redux/productFilters/productFiltersSlice";
import {
  productFilterOptionSelectors,
  productFilterSelectors,
} from "@/lib/redux/store";
import { EntityId } from "@reduxjs/toolkit";
import { useEffect, useMemo, useRef } from "react";
import ControlComponent from "./Control";
import MenuComponent, { GroupValuesProps } from "./Menu/Menu";
import { Container } from "./styles";

// Cast to fix React 17 + @types/react 18 compatibility issue
const Control = ControlComponent as any;
const Menu = MenuComponent as any;

export type GroupProps = {
  title: string;
  groups: GroupValuesProps[];
};

type Props = {
  filterId: EntityId;
  isOpen: boolean;
  handleOpen: (filter: ProductFilter) => void;
  groups: GroupProps[];
};

const Dropdown: React.FC<Props> = (props) => {
  const { filterId, isOpen, handleOpen, groups } = props;
  const menuRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const filter = useAppSelector((state) =>
    productFilterSelectors.selectById(state, filterId)
  );

  const options = useMemo(() => {
    if (filter) {
      return productFilterOptionSelectors.selectAll(filter);
    }
  }, [filter]);

  const hasSelectedOptions = !!options?.some((option) => option.selected);
  const group = groups?.filter((group) => group.title === filter?.title);

  useEffect(() => {
    // Create closure around mutable ref
    const menu = menuRef.current;
    const container = containerRef.current;
    if (!menu || !container) return;

    const state = Flip.getState([container, menu]);

    if (isOpen) {
      menu.style.display = "grid";
    } else {
      menu.style.display = "none";
    }

    Flip.from(state, {
      duration: 0.2,
      onEnter: (elements) =>
        gsap.fromTo(elements, { opacity: 0 }, { opacity: 1 }),
      onLeave: (elements) =>
        gsap.fromTo(elements, { opacity: 1 }, { opacity: 0 }),
    });
  }, [isOpen]);

  if (filter?.options?.ids?.length) {
    return (
      <Container ref={containerRef}>
        <Control
          label={filter.title}
          handleClick={() => handleOpen(filter)}
          pinned={hasSelectedOptions}
          menuIsOpen={isOpen}
        />
        <Menu ref={menuRef} filter={filter} groupValues={group[0]?.groups} />
      </Container>
    );
  } else {
    return null;
  }
};

export default Dropdown;
